import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = window.location.origin || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function WebsiteSection({ user }) {
  const [loading, setLoading] = useState(false);
  const [websites, setWebsites] = useState([]);
  const [generatedWebsite, setGeneratedWebsite] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    fetchUserWebsites();
  }, []);

  const fetchUserWebsites = async () => {
    try {
      const response = await axios.get(`${API}/website/user/${user.id}`);
      setWebsites(response.data.websites);
    } catch (error) {
      console.error('Error fetching websites:', error);
    }
  };

  const handleGenerateWebsite = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/website/generate`, {
        user_id: user.id
      });

      setGeneratedWebsite(response.data);
      setShowPreview(true);
      fetchUserWebsites();
    } catch (error) {
      alert('Failed to generate website: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const response = await axios.post(`${API}/website/publish`, {
        website_id: generatedWebsite.website_id
      });

      alert('🎉 ' + response.data.message);
      setShowPreview(false);
      fetchUserWebsites();
    } catch (error) {
      alert('Failed to publish website: ' + error.message);
    } finally {
      setPublishing(false);
    }
  };

  const handleDeleteWebsite = async (websiteId) => {
    if (!window.confirm('Are you sure you want to delete this website? This cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/website/delete/${websiteId}`);
      alert('✅ Website deleted successfully!');
      fetchUserWebsites();
    } catch (error) {
      alert('Failed to delete website: ' + error.message);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-4xl font-bold accent-text mb-2">🌐 Website Builder</h2>
        <p className="style={{ color: 'var(--text-muted)' }}">Create a professional website with AI in 30 seconds</p>
      </div>

      {/* Preview Modal */}
      {showPreview && generatedWebsite && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl border border-orange-500/50 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between sticky top-0 bg-gray-900 z-10">
              <div>
                <h3 className="text-2xl font-bold text-white">Website Preview</h3>
                <p className="style={{ color: 'var(--text-muted)' }} text-sm">Your website is ready!</p>
              </div>
              <button
                onClick={() => setShowPreview(false)}
                className="style={{ color: 'var(--text-muted)' }} hover:text-white text-2xl"
              >
                ✕
              </button>
            </div>

            {/* Preview Content */}
            <div className="p-6">
              {/* Website Info */}
              <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 border border-blue-500/50 rounded-xl p-4 mb-6">
                <div className="text-white font-semibold mb-2">📍 Your Website Preview URL:</div>
                <div className="text-blue-400 text-sm font-mono break-all">{`${BACKEND_URL}${generatedWebsite.preview_url}`}</div>
                <button
                  onClick={() => window.open(`${BACKEND_URL}${generatedWebsite.preview_url}`, '_blank')}
                  className="mt-3 w-full px-4 py-2 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600 transition"
                >
                  👁️ Open Website Preview
                </button>
                <div className="mt-3 p-3 bg-yellow-900/30 border border-yellow-500/50 rounded-lg">
                  <div className="text-yellow-400 text-xs font-semibold mb-1">💡 Production Note:</div>
                  <div className="text-yellow-200 text-xs">
                    Future subdomain: <span className="font-mono">{generatedWebsite.subdomain}</span>
                    <br />
                    (Requires DNS configuration on maxedigital.com)
                  </div>
                </div>
              </div>

              {/* Content Preview */}
              <div className="space-y-4 mb-6">
                <div className="theme-card border rounded-lg p-4">
                  <h4 className="text-orange-400 font-semibold mb-2">🎯 Hero Section</h4>
                  <p className="text-white font-bold text-xl">{generatedWebsite.content.hero.headline}</p>
                  <p className="text-gray-300 mt-2">{generatedWebsite.content.hero.subheadline}</p>
                  <button className="mt-3 px-4 py-2 bg-orange-500 text-white rounded-lg">
                    {generatedWebsite.content.hero.cta}
                  </button>
                </div>

                <div className="theme-card border rounded-lg p-4">
                  <h4 className="text-orange-400 font-semibold mb-2">📖 About Section</h4>
                  <p className="text-white font-semibold">{generatedWebsite.content.about.title}</p>
                  <p className="text-gray-300 mt-2">{generatedWebsite.content.about.description}</p>
                </div>

                <div className="theme-card border rounded-lg p-4">
                  <h4 className="text-orange-400 font-semibold mb-3">💼 Services</h4>
                  <div className="grid grid-cols-3 gap-3">
                    {generatedWebsite.content.services.map((service, idx) => (
                      <div key={idx} className="bg-gray-700 p-3 rounded-lg text-center">
                        <div className="text-3xl mb-2">{service.icon}</div>
                        <div className="text-white font-semibold text-sm">{service.title}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4">
                <button
                  onClick={handlePublish}
                  disabled={publishing}
                  className="flex-1 py-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-bold text-lg hover:from-green-600 hover:to-emerald-600 transition disabled:opacity-50"
                >
                  {publishing ? 'Publishing...' : '✅ Publish Website'}
                </button>
                <button
                  onClick={() => setShowPreview(false)}
                  className="px-6 py-4 bg-gray-700 text-white rounded-lg font-semibold hover:bg-gray-600 transition"
                >
                  Close Preview
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* How It Works Info */}
      <div className="bg-blue-900/30 border border-blue-500/50 rounded-xl p-4 mb-6">
        <h4 className="text-white font-semibold mb-2">💡 How Websites Work:</h4>
        <div className="text-gray-300 text-sm space-y-1">
          <p>✅ Websites are stored in YOUR database (not external servers)</p>
          <p>✅ Preview URLs work immediately on your current domain</p>
          <p>✅ You can delete websites anytime using the Delete button</p>
          <p>✅ All website data stays on YOUR server at maxedigital.com</p>
        </div>
      </div>

      {/* Main Content */}
      {websites.length === 0 ? (
        /* No Websites Yet - Hero CTA */
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-orange-500/20 rounded-2xl p-12 text-center">
          <div className="text-8xl mb-6">🌐</div>
          <h3 className="text-3xl font-bold accent-text mb-4">Create Your Professional Website</h3>
          <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
            Let AI build you a stunning website in 30 seconds. Complete with:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8 max-w-4xl mx-auto">
            <div className="theme-card-nested border rounded-lg p-4">
              <div className="text-3xl mb-2">🎨</div>
              <div className="text-white font-semibold">Your Branding</div>
              <div className="text-sm style={{ color: 'var(--text-muted)' }}">Logo & Colors</div>
            </div>
            <div className="theme-card-nested border rounded-lg p-4">
              <div className="text-3xl mb-2">📝</div>
              <div className="text-white font-semibold">AI Content</div>
              <div className="text-sm style={{ color: 'var(--text-muted)' }}">Professional Copy</div>
            </div>
            <div className="theme-card-nested border rounded-lg p-4">
              <div className="text-3xl mb-2">📸</div>
              <div className="text-white font-semibold">Your Gallery</div>
              <div className="text-sm style={{ color: 'var(--text-muted)' }}">From Your Posts</div>
            </div>
            <div className="theme-card-nested border rounded-lg p-4">
              <div className="text-3xl mb-2">📱</div>
              <div className="text-white font-semibold">Responsive</div>
              <div className="text-sm style={{ color: 'var(--text-muted)' }}">Mobile-Friendly</div>
            </div>
          </div>

          <button
            onClick={handleGenerateWebsite}
            disabled={loading}
            className="px-12 py-5 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-xl font-bold text-xl hover:from-orange-600 hover:to-pink-600 transition disabled:opacity-50 shadow-lg"
          >
            {loading ? (
              <span>✨ Generating Your Website...</span>
            ) : (
              <span>🚀 Create My Website Now</span>
            )}
          </button>

          <p className="text-gray-500 text-sm mt-4">Takes about 30 seconds</p>
        </div>
      ) : (
        /* Existing Websites */
        <div className="space-y-6">
          {/* Generate New Website Button */}
          <button
            onClick={handleGenerateWebsite}
            disabled={loading}
            className="w-full py-4 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-xl font-bold text-lg hover:from-orange-600 hover:to-pink-600 transition disabled:opacity-50"
          >
            {loading ? '✨ Generating New Website...' : '🚀 Generate New Website'}
          </button>

          {/* Websites List */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {websites.map((website) => (
              <div
                key={website.id}
                className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-xl p-6 hover:border-orange-500/50 transition"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-white">{website.brand_name}</h3>
                    <p className="text-sm style={{ color: 'var(--text-muted)' }}">
                      Created {new Date(website.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  {website.published && (
                    <span className="px-3 py-1 bg-green-500/20 text-green-400 text-sm font-semibold rounded-full">
                      ✓ Published
                    </span>
                  )}
                </div>

                <div className="bg-gray-800/50 rounded-lg p-3 mb-4">
                  <div className="text-xs text-gray-500 mb-1">Preview URL:</div>
                  <div className="text-blue-400 font-mono text-xs break-all">
                    {`${BACKEND_URL}/api/website/view/${website.id}`}
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => window.open(`${BACKEND_URL}/api/website/view/${website.id}`, '_blank')}
                    className="flex-1 py-2 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600 transition"
                  >
                    👁️ Preview
                  </button>
                  <button
                    onClick={() => handleDeleteWebsite(website.id)}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition"
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default WebsiteSection;
